<template>
  <f7-page hide-bars-on-scroll>
    <f7-navbar title no-shadow back-link>{{
      $t("common.edit") + " " + (profile.given_name || '') + " " + (profile.family_name || '')
    }}</f7-navbar>
    <AppLayout>
      <template #main>
        <f7-list list media-list form strong dividers inset outline id="userform1">
          <f7-list-group>
            <f7-list-item :title="$t('profile.myPersonalInfo')" divider></f7-list-item>

            <f7-list-input
              :label="$t('common.firstName')"
              type="text"
              name="firstName"
              v-model:value="form.firstName"
              required
            />

            <f7-list-input
              :label="$t('common.lastName')"
              type="text"
              name="lastName"
              v-model:value="form.lastName"
              required
            />

            <f7-list-input
              :label="$t('common.dateOfBirth')"
              type="date"
              name="dateOfBirth"
              v-model:value="form.dateOfBirth"
            />

            <f7-list-input
              :label="$t('common.email')"
              type="email"
              name="email"
              :placeholder="$t('common.email')"
              v-model:value="form.email"
              required
            />

            <f7-list-input
              :label="$t('profile.myPhoneNumber')"
              type="tel"
              name="phoneNumber"
              ref="phoneInputRef"
              v-model:value="form.phoneNumber"
              :placeholder="$t('profile.phoneNumberPlaceholder')"
            />

            <f7-list-item :title="$t('common.country')" smart-select :smart-select-params="{ closeOnSelect: true }">
              <select name="country" v-model="form.countryCode">
                <option v-for="c in countries" :key="c.code" :value="c.code">
                  {{ c.name }}
                </option>
              </select>
            </f7-list-item>

            <f7-list-item :title="$t('common.locality')" smart-select
              :smart-select-params="{ closeOnSelect: true, openIn: 'popup', popupSwipeToClose: true, popupPush: true, searchbar: true, searchbarPlaceholder: $t('common.locality') }">
              <select id="stateid" name="city" v-model="form.locality" @change="onCityChange">
                <optgroup v-for="(states, stateName, index) in destinationCity.stateList" :data-stateid="states.stateId"
                  :label="states.stateName" :key="index">
                  <option smartselect v-for="citylist in states.cityList" :value="citylist.cityName"
                    :data-cityname="citylist.cityName" :key="citylist.cityId">
                    {{ citylist.cityName }}
                  </option>
                </optgroup>
              </select>
            </f7-list-item>

            <f7-list-input
              :label="$t('common.postCode')"
              type="text"
              name="postal_code"
              :placeholder="$t('common.postCode')"
              v-model:value="form.postalCode"
              clear-button
            />

            <f7-list-input
              :label="$t('common.addressLine') + ' 1'"
              type="text"
              name="street_address"
              :placeholder="$t('common.address')"
              v-model:value="form.streetAddress"
              clear-button
            />

            <f7-list-input
              :label="$t('common.addressLine') + ' 2'"
              type="text"
              name="addressLine2"
              :placeholder="$t('common.address')"
              v-model:value="form.addressLine2"
              clear-button
            />
          </f7-list-group>
        </f7-list>

        <f7-button raised fill large round ripple @click="saveMyChanges()" class="margin-vertical">
          {{ submitting ? $t("common.loading") : $t("common.update") }}
        </f7-button>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useAccountStore } from "../js/pinia-stores/account.js";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import { useLocationStore } from "../js/pinia-stores/location.js";
import { useTelephone } from "../js/composables/useTelephone.js";
import AppLayout from "@/components/common/AppLayout.vue";

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const keycloakStore= useKeycloakStore();
const accountStore = useAccountStore();
const locationStore = useLocationStore();
const authStore = useAuthStore();
const profile = computed(() => keycloakStore.profile);

const phoneInputRef = ref(null);
useTelephone(phoneInputRef);

// Countries list (mirrors the existing data used in the app)
const countries = ref([
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'SN', name: 'Sénégal' },
  { code: 'ML', name: 'Mali' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'NE', name: 'Niger' },
  { code: 'TG', name: 'Togo' },
  { code: 'BJ', name: 'Bénin' },
  { code: 'GN', name: 'Guinée' },
  { code: 'CM', name: 'Cameroun' },
  { code: 'GA', name: 'Gabon' },
  { code: 'FR', name: 'France' },
]);

const submitting = ref(false);
const saveError = ref(null);
const destinationCity = computed(() => locationStore.destinationCity);

// Form fields — initialised from the reactive profile object on mount.
// Since profile is a computed ref backed by the Pinia store, mutating these
// fields updates the source of truth directly (no stale local refs).
const form = reactive({
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  phoneNumber: '',
  countryCode: '',
  locality: '',
  postalCode: '',
  streetAddress: '',
  addressLine2: '',
});

onMounted(async () => {
  // Ensure the Keycloak profile is loaded after a step-up redirect; it's
  // populated asynchronously and may not be ready when this page mounts.
  if (!keycloakStore.profile) {
    await keycloakStore.loadUserProfile().catch(() => {});
  }
  syncFormFromProfile();
  authStore.getAdminToken();
  locationStore.getCountryStates();
});

function syncFormFromProfile() {
  const p = profile.value || {};
  const addr = p.address || {};
  form.firstName      = p.given_name || '';
  form.lastName       = p.family_name || '';
  form.dateOfBirth    = p.dateOfBirth || '';
  form.email          = p.email || '';
  form.phoneNumber    = p.phoneNumber || p.upn || '';
  form.countryCode    = p.countryCode || addr.country || '';
  form.locality       = addr.locality || '';
  form.postalCode     = addr.postal_code || '';
  form.streetAddress  = addr.street_address || '';
  form.addressLine2   = addr.addressLine2 || '';
}

function onCityChange() {
  // City selection handled by the smart-select v-model binding on form.locality
}

async function saveMyChanges() {
  submitting.value = true;
  saveError.value = null;

  try {
    const loc = destinationCity.value || [];
    const matchedCountry = loc.find(c => c.countryCode2 === form.countryCode);

    const updatedAttributes = {
      phoneNumber:   form.phoneNumber,
      dateOfBirth:   form.dateOfBirth || undefined,
      countryCode:   form.countryCode,
      addressStreet:   form.streetAddress,
      addressLocality: form.locality,
      addressPostalCode: form.postalCode,
      addressCountry: matchedCountry?.countryName || form.countryCode,
      addressRegion: matchedCountry?.stateName || '',
    };

    const payload = {
      firstName: form.firstName,
      lastName:  form.lastName,
      email:     form.email,
      updatedAttributes,
    };

    await accountStore.updateUserProfile(payload);

    // Refresh the Keycloak admin user cache so profile reflects the latest data
    authStore.adminUser = null;
    await authStore.getUserFromAdmin();

    f7.toast.show({ text: t('Profile updated successfully'), closeTimeout: 2000 });
    setTimeout(() => props.f7router.navigate('/dashboard/'), 400);
  } catch (e) {
    saveError.value = e?.response?.data?.message || e?.message || t('Update failed. Please try again.');
    f7.dialog.alert(saveError.value);
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
</style>